import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { dataExportsAPI } from '../../../api';
import { MoonLoader } from 'react-spinners';
import CustomModal from "../CustomModal/CustomModal"; // Import the modal component
import FieldEditor from './FieldEditor';
import MultiSelectDropdown from '../BusinessSelector/MultipleChoicesDropdown';
import Receivers from './Receivers';
import FrequencySelector from './FrequencySelector';

const BIG_BACKGROUND_COLOR = "#1F2937";
const SMALL_BACKGROUND_COLOR = "#374151";


const SettingForm = ({scheduledSetting=null, setEditingScheduledSetting=null, setRefresh, setShowSettingForm}) => {
    // If the user is not logged in, redirect to the login page
    useEffect(() => {
        if (!localStorage.getItem('email')) window.location.href = '/auth/signin';
    }, []);

    const fetchUserResponse = useSelector(state => state.fetchUserResponse);

    const businesses = fetchUserResponse?.businesses;
    // Get role and business roles
    const role = localStorage.getItem('rbac_role');
    let businessRoles = [];
    try {
      businessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
    } catch {
      businessRoles = [];
    }

    // Filter businesses based on role
    let businessNames = businesses?.map(business => Object.keys(business)[0]) || [];

    if (role?.toLowerCase() !== 'tla') {
      // For non-TLA users, filter based on businesses where they have non-technician roles
      const allowedBusinesses = businessRoles
        .filter(br => br.role?.toLowerCase() !== 'technician') // Exclude technician roles
        .map(br => br.business);
      
      businessNames = businessNames.filter(name => allowedBusinesses.includes(name));
    }

    businessNames = businessNames?.sort();
    
    
    
    const initBusinessName = scheduledSetting ? scheduledSetting.business_name : (businessNames && businessNames.length > 0 ? businessNames[0] : '');
    const initSelectedSites = scheduledSetting && scheduledSetting?.sites?.map(site => ({site: site.site_name, hwid: site.hwid})) || [];
    const initCustomSelectedTables = scheduledSetting && scheduledSetting?.table_configs || [];
    const initEmails = scheduledSetting && scheduledSetting?.email_recipients || [];
    const initFrequency = scheduledSetting && scheduledSetting?.frequency ? scheduledSetting?.frequency.split('_') : [];

    const [selectedBusiness, setSelectedBusiness] = useState(initBusinessName);
    const [selectedSites, setSelectedSites] = useState(initSelectedSites);
    const [loadingTables, setLoadingTables] = useState(false);
    const [tableInfo, setTableInfo] = useState([]);


    useEffect(() => {
      // get init selected tables, if scheduledSetting is not null, 
      // it is equal to the tables which is saved in scheduledSetting in table_info
      // if scheduledSetting is null, it is equal to []
      if (!tableInfo || tableInfo.length === 0) return;
      if (!scheduledSetting) return;
      const initSelectedTables = tableInfo.filter((table)=>{return (scheduledSetting?.table_configs?.map(table => table.name) || []).includes(table.name)})
      
      setSelectedTables(initSelectedTables);

    }, [tableInfo]);
    
    const filteredSites = businesses?.filter(obj => obj[selectedBusiness] !== undefined);

    useEffect(() => {
        if (businessNames && !selectedBusiness && !scheduledSetting) {
          setSelectedBusiness(businessNames[0]);
        }
    }, [businessNames]);

    const extractAndSortBySite = (filteredSites) => {
        return filteredSites
          .flatMap(item => {
            const key = Object.keys(item)[0]; // Get the key, e.g., "HIVE"
            const siteDataArray = item[key]; // Get all site data from that key (as an array)
            return siteDataArray.map(siteData => ({
                site: siteData.site,
                hwid: siteData.hwid
            }));
        }).sort((a, b) => a.site.localeCompare(b.site)); // Sort by the site property
    };


    // Use useMemo to memorize the sorted sites
    const sortedSites = useMemo(() => {
        if (filteredSites) {
            return extractAndSortBySite(filteredSites);
        }
        return []; // Return an empty array if no filteredSites
    }, [selectedBusiness]); // Recompute when filteredSites changes
    

    const handleBusinessChange = (event) => {
        setSelectedBusiness(event.target.value);
        handleSiteSelectionChange([]);
        setSelectedSites([]);
        
        setSelectedTables([]);
        setCustomSelectedTables([]);

        setSelectAll(false);
        setTableInfo([]);
      };


      
      const getTables = async (business_name) => {
        setLoadingTables(true);
        try {
            let res = await dataExportsAPI.post('/tables', {business_name, selectedSites});

            if (res.status === 200 || res.status === 500) {
                let data = res.data.data;
                data = data.map(site=> ({...site, tables: site.tables.map(table=>({...table, columns: table.columns.map(column=>({...column, columnName: column.field}))}))}))

                if (selectedSites.length > 1) {
                    // Create a Set to hold unique table names
                    let uniqueTableNames = new Set();
                    // Create an array to store unique tables
                    let uniqueTables = [];

                    // Iterate over the list of objects
                    data.forEach(obj => {
                        obj.tables.forEach(table => {
                            if (!uniqueTableNames.has(table.name)) {
                            uniqueTableNames.add(table.name); // Add table name to the Set
                            uniqueTables.push(table); // Add the table to the result array
                        }});
                    });

                    setTableInfo(uniqueTables);

                } else {
                    setTableInfo(data[0].tables);
                }

            } else {
                toast.error("Failed to get tables");
            }

        } catch (error) {
            // console.log("error", error);
        }
        setLoadingTables(false);

    }
    
    useMemo(() => {
        if (selectedSites.length > 0 && selectedBusiness !== '') {
            getTables(selectedBusiness, selectedSites);
        }
    }, 
    [selectedSites, selectedBusiness]);


    const [selectedTables, setSelectedTables] = useState([]);
  
    // State to manage the "Select All" checkbox
    const [selectAll, setSelectAll] = useState(false);
  
    // Handle individual checkbox selection
    const handleCheckboxChange = (tableObj) => {
      setSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });

      setCustomSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });
    };
  
    // Handle "Select All" checkbox change
    const handleSelectAllChange = () => {
      if (selectAll) {
        setSelectedTables([]); // Deselect all
        setCustomSelectedTables([]);
      } else {
        setSelectedTables(tableInfo.map((t) => t)); // Select all
        // Keep the tables in customSelectedTables, add new tables to it
        setCustomSelectedTables((prevSelectedTables) => {
          // Get the names of the tables already in customSelectedTables
          const prevTableNames = prevSelectedTables.map(t => t.name);
          // Get the names of the tables that are not already in customSelectedTables
          const newTables = tableInfo.filter(t => !prevTableNames.includes(t.name));
          // Return the union of the two sets
          return [...prevSelectedTables, ...newTables];
        });
      }
      setSelectAll(!selectAll); // Toggle the "Select All" state
    };

    const selectedCount = selectedTables.length;


    const [showModal, setShowModal] = useState(false);
    const [tablePoppedUp, setTablePoppedUp] = useState({}); //advanced table info
    const [selectedColumnObjs, setSelectedColumnObjs] = useState([]);
    const [customSelectedTables, setCustomSelectedTables] = useState(initCustomSelectedTables);


    const handleSiteSelectionChange = (selected) => {
      setSelectedSites(selected);
    };

    const isTableChecked = (tableObj) => {
      return (selectedTables && selectedTables.length !== 0) ? selectedTables.map((t)=>t.name).includes(tableObj.name) : false
    }

    const handleOpenAdvanced = (tableObj) => {
      setTablePoppedUp(tableObj); 
      // find table in customSelectedTables with the same name as tableObj
      let table = customSelectedTables.find(t => t.name === tableObj.name);
      if (table) setSelectedColumnObjs(table.columns)
      else setSelectedColumnObjs(tableObj.columns);
      setShowModal(true)
    }

    const [emails, setEmails] = useState(initEmails);
    const [frequency, setFrequency] = useState(initFrequency);

      const sortFrequency = (frequency) => {
        return frequency.sort((a, b) => {
        const order = ['daily', 'weekly', 'monthly'];
        return order.indexOf(a) - order.indexOf(b);
        });
      };
      

    const saveSetting = async () => {

      try{
        let hw_ids = selectedSites.map(site => site.hwid);
        let formatted_customSelectedTables = customSelectedTables.map(table => ({
          name: table.name,
          columns: table.columns.map(column => ({
            field: column.field,
            columnName: column.columnName
          }))
        }));

        let sortedFrequency = sortFrequency(frequency);
        let formatted_frequency = sortedFrequency.join('_');

        let data_passed ={
          business_name: selectedBusiness,
          user_email: localStorage.getItem('email'),
          hw_ids: hw_ids,
          table_configs: formatted_customSelectedTables,
          frequency: formatted_frequency,
          email_recipients: emails
        }
        // console.log("data_passed === ", data_passed);
        const response = await dataExportsAPI.post("/csv-delivery-settings", data_passed);
        
        if (response.status === 201) {
          toast.success("Setting saved successfully. Refreshing page...");
          setRefresh(new Date().getTime());
          setShowSettingForm(false);

        } else {
          toast.error("Failed to save setting");
        }

      } catch (error) {
        // console.log("error", error);
        toast.error(`Failed to save setting. ${error?.response?.data?.error?.message || ""}`);
      }
    
    }

    const updateSetting = async () => {
      try{
        let hw_ids = selectedSites.map(site => site.hwid);
        let formatted_customSelectedTables = customSelectedTables.map(table => ({
          name: table.name,
          columns: table.columns.map(column => ({
            field: column.field,
            columnName: column.columnName
          }))
        }));

        let sortedFrequency = sortFrequency(frequency);
        let formatted_frequency = sortedFrequency.join('_');

        let data_passed ={
          business_name: selectedBusiness,
          hw_ids: hw_ids,
          table_configs: formatted_customSelectedTables,
          frequency: formatted_frequency,
          email_recipients: emails
        }
        // console.log("data_passed === ", data_passed);
        const response = await dataExportsAPI.put(`/csv-delivery-settings/${scheduledSetting.id}`, data_passed);
        
        if (response.status === 200) {
          toast.success("Setting updated successfully. Refreshing page...");
          setEditingScheduledSetting({});
          setRefresh(new Date().getTime());

        } else {
          toast.error("Failed to update setting");
        }

      } catch (error) {
        // console.log("error", error);
        toast.error(`Failed to update setting. ${error?.response?.data?.error || ""}`);
      }
    }


    const dropChanges = () => {
      setEditingScheduledSetting({});
      setShowSettingForm(false);
    }


    const deleteSetting = async () => {
      try{
        const response = await dataExportsAPI.delete(`/csv-delivery-settings/${scheduledSetting.id}`);
        
        if (response.status === 200) {
          toast.success("Setting deleted successfully. Refreshing page...");
          setEditingScheduledSetting({});
          setRefresh(new Date().getTime())
          
        } else {
          toast.error("Failed to delete setting");
        }
        
      } catch (error) {
        // console.log("error", error);
        toast.error("Failed to delete setting");
      }
    }

    return (
        <div className='setting-form-container'>
          <div className='setting-form-header'>
            <h2>
              {scheduledSetting && scheduledSetting.id ? "Edit" : "Create"} Scheduled Delivery for {selectedBusiness}
            </h2>
            {
              scheduledSetting && scheduledSetting.id 
              ? <div className='setting-form-header-actions'>
                <button className='setting-form-btn-cancel' onClick={dropChanges}>Cancel</button>
                <button className='setting-form-btn-delete' onClick={deleteSetting}>Delete</button>
              </div>
              : <div className='setting-form-header-actions'>
                <button className='setting-form-btn-update' onClick={saveSetting}>SAVE</button>
                <button className='setting-form-btn-cancel' onClick={dropChanges}>Cancel</button>
              </div>
            }
          </div>
                        
            <div className='setting-form-config-section'>
                <h3>Data Source</h3>
                <div className='setting-form-input-group'>
                    <label >Select a business:</label>
                    <select className='setting-form-dark-select' value={selectedBusiness} style={{width: "100%", padding: "5px 3px"}}
                      onChange={(event)=>{handleBusinessChange(event)}} disabled={loadingTables}>
                        <option value="">Please select..</option>
                    {businessNames?.map((business, index) => (
                        <option key={index} value={business}>{business}</option>
                    ))}
                    </select>
                </div>
                
                <div className='setting-form-input-group'>
                  <label>Select sites:</label>
                  <MultiSelectDropdown 
                    sites={sortedSites} 
                    onChange={handleSiteSelectionChange} 
                    loading={loadingTables} 
                    selectedSites={selectedSites}
                    setSelectedSites={setSelectedSites}
                  />
                </div>
                
                <div >
                    <MoonLoader loading={loadingTables} size={16} color={"white"} />
                </div>
            </div>
            
            <CustomModal
              show={showModal}
              handleClose={() => {
                setShowModal(false); 
                setCustomSelectedTables(customSelectedTables.map(table => table.name === tablePoppedUp.name ? { ...table, columns: selectedColumnObjs } : table));
                setSelectedColumnObjs([]);
              }}
              title={tablePoppedUp.name}
            >
              {
                <FieldEditor tablePoppedUp={tablePoppedUp} 
                selectedColumnObjs={selectedColumnObjs} setSelectedColumnObjs={setSelectedColumnObjs} />
              }

            </CustomModal>
            
            <div className='setting-form-config-section'>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <h3>Select Tables</h3>
              </div>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <div style={{marginBottom: "10px"}}>
                    <input
                        type="checkbox"
                        id="selectAll"
                        name="selectAll"
                        value="selectAll"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        disabled={!tableInfo || tableInfo.length === 0}
                    />
                      <label htmlFor="selectAll"> Select All</label>
                  </div>
                  <div className='setting-form-table-count'>
                    <div style={{marginRight:"5px"}}>{selectedCount} tables selected</div>
                  </div>
              </div>

              <div>
                  {
                      loadingTables ? (
                          <div>Loading tables...</div>
                      ) : tableInfo?.length === 0 ? (
                          <div>No tables found</div>
                      ) : (
                          <div className='setting-form-tables-grid'>
                            {tableInfo?.map((tableObj, index) => (
                                <div className='setting-form-table-item' key={index}>
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                    <input
                                      type="checkbox"
                                      id={tableObj.name}
                                      name={tableObj.name}
                                      value={tableObj.name}
                                      checked={isTableChecked(tableObj)}
                                      onChange={() => handleCheckboxChange(tableObj)}
                                    />
                                    <label htmlFor={tableObj.name}>{tableObj.name}</label>
                                  </div>
                                  {
                                    <span className='setting-form-table-tag'
                                    onClick={()=>{handleOpenAdvanced(tableObj)}}>Advanced</span>
                                  }
                                </div>
                            ))}
                          </div>
                      )
                  }
              </div>
            </div>
            
            <FrequencySelector frequency={frequency} setFrequency={setFrequency} 
              customStyles={{"backgroundColor": SMALL_BACKGROUND_COLOR, width:"600px", borderRadius: "10px", padding: "20px", marginBottom: "10px", }} />
            <Receivers emails={emails} setEmails={setEmails}
              customStyles={{"backgroundColor": SMALL_BACKGROUND_COLOR, width:"600px", borderRadius: "10px", padding: "20px", marginBottom: "10px", width:"600px"}}/>


            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
              {
                scheduledSetting && scheduledSetting.id 
                && <div className='setting-form-header-actions'>
                <button className='setting-form-btn-update' onClick={updateSetting}>Update</button>
              </div>
              }
            </div>
        </div>
    )
}


export default SettingForm;

